import { useState, useEffect, useMemo } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  UserCheck, ShieldQuestion, Lock, Sparkles, BookmarkPlus, CheckCircle2, FileText, KeyRound, Info, Clock, UploadCloud,
} from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { buildBreakdown, buildSyncPayload } from "@/lib/analysis";
import { estimateRecovery } from "@/lib/simulation";
import {
  OPERATOR_KEY_ID, OPERATOR_PUBKEY, OPERATOR_FINGERPRINT, OPERATOR_SESSION_START,
} from "@/lib/operatorKey";

export const HITLReview = ({ pendingFault, domain, onApprove, submitting, lastSealed, onSaveLibrary, recovering, recoveryRemaining, recoveryTotal, currentVx }) => {
  const [note, setNote] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setAnalysis(null);
    setSaved(false);
  }, [lastSealed?.id]);

  const runAnalyze = () => setAnalysis(buildBreakdown(lastSealed, domain));

  const saveToLibrary = async () => {
    if (!analysis) return;
    setSaving(true);
    try {
      await onSaveLibrary({
        domain: domain.name,
        fault_type: lastSealed.fault_type,
        event_timestamp: lastSealed.timestamp,
        batch_seq: lastSealed.batch_seq,
        merkle_root: lastSealed.merkle_root,
        breakdown: analysis,
      });
      setSaved(true);
    } catch {
      /* toast handled upstream */
    } finally {
      setSaving(false);
    }
  };

  const handle = async () => {
    if (!note.trim()) return;
    await onApprove(note.trim());
    setNote("");
  };

  return (
    <div className="glass-panel rounded-2xl p-5 h-full flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <h3 className="mono text-xs font-semibold uppercase tracking-widest text-cyan-400">
          HITL Review · Human Interlock
        </h3>
        <UserCheck className="h-4 w-4 text-teal-400" />
      </div>

      {!pendingFault ? (
        recovering ? (
          <LiveRecoveryPanel
            remaining={recoveryRemaining}
            total={recoveryTotal}
            currentVx={currentVx}
          />
        ) : lastSealed ? (
          <AnalyzePanel
            lastSealed={lastSealed}
            analysis={analysis}
            onAnalyze={runAnalyze}
            onSave={saveToLibrary}
            saving={saving}
            saved={saved}
          />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-6">
            <ShieldQuestion className="h-9 w-9 text-slate-600 mb-3" />
            <p className="text-sm text-slate-400 font-medium">No pending review</p>
            <p className="mono text-[11px] text-slate-600 mt-1">
              Trigger a fault to open a human-in-the-loop correction.
            </p>
          </div>
        )
      ) : (
        <div className="flex-1 flex flex-col">
          <div className="grid grid-cols-2 gap-2 mb-4">
            <Detail label="Domain" value={domain.short} />
            <Detail label="Fault Type" value={pendingFault.faultType} danger />
            <Detail label="V(x) @ Fault" value={pendingFault.spikeValue} danger />
            <Detail label="Governor Action" value="Command Quarantined" />
          </div>
          <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-3 mb-4">
            <p className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">
              Interlock Log
            </p>
            <p className="mono text-[11px] text-slate-400 leading-relaxed">
              {pendingFault.governorAction}
            </p>
          </div>

          <RecoveryWindow spikeValue={pendingFault.spikeValue} />

          <label className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">
            Correction Note
          </label>
          <Popover>
            <PopoverTrigger asChild>
              <button
                data-testid="operator-key-badge"
                className="mb-2 inline-flex items-center gap-1.5 rounded-md border border-cyan-700/50 bg-cyan-950/30 px-2 py-1 mono text-[10px] text-cyan-300 self-start hover:border-cyan-500/70 transition-colors"
              >
                <KeyRound className="h-3 w-3" />
                Operator Key: {OPERATOR_KEY_ID} (Ed25519) — Auto-Loaded
                <Info className="h-3 w-3 opacity-70" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-[340px] bg-[#0b1120] border-slate-800 text-slate-200"
              data-testid="operator-key-popover"
            >
              <p className="mono text-[10px] uppercase tracking-widest text-cyan-400 mb-3">
                Operator Signing Key
              </p>
              <div className="space-y-2.5">
                <div className="flex justify-between gap-3">
                  <span className="mono text-[9px] uppercase tracking-widest text-slate-500">Key ID</span>
                  <span className="mono text-[10px] text-slate-200">{OPERATOR_KEY_ID}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="mono text-[9px] uppercase tracking-widest text-slate-500">Algorithm</span>
                  <span className="mono text-[10px] text-slate-200">Ed25519 (EdDSA)</span>
                </div>
                <div>
                  <p className="mono text-[9px] uppercase tracking-widest text-slate-500 mb-1">Public key</p>
                  <p className="mono text-[10px] text-cyan-300 break-all leading-relaxed">{OPERATOR_PUBKEY}</p>
                </div>
                <div>
                  <p className="mono text-[9px] uppercase tracking-widest text-slate-500 mb-1">Fingerprint · SHA-256</p>
                  <p className="mono text-[10px] text-violet-300 break-all leading-relaxed">{OPERATOR_FINGERPRINT}</p>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="mono text-[9px] uppercase tracking-widest text-slate-500">Session started</span>
                  <span className="mono text-[10px] text-slate-200">
                    {new Date(OPERATOR_SESSION_START).toISOString().replace("T", " ").slice(0, 19)}Z
                  </span>
                </div>
              </div>
              <p className="text-[10px] text-slate-500 mt-3 leading-relaxed border-t border-slate-800 pt-2">
                Ephemeral key — regenerated on every page load and never persisted. Cite this fingerprint to
                identify which reviewer session signed an entry.
              </p>
            </PopoverContent>
          </Popover>
          <Textarea
            data-testid="hitl-correction-note-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Describe the corrective action taken to restore Lyapunov stability…"
            className="flex-1 min-h-[80px] bg-slate-950/60 border-slate-800 text-slate-200 mono text-xs resize-none focus-visible:ring-cyan-500/50"
          />

          <Button
            data-testid="hitl-approve-button"
            onClick={handle}
            disabled={!note.trim() || submitting}
            className="mt-3 h-11 mono font-bold tracking-widest bg-cyan-500 hover:bg-cyan-400 text-[#060A12] disabled:opacity-40 transition-all"
          >
            <Lock className="h-4 w-4 mr-2" />
            {submitting ? "SEALING…" : "APPROVE CORRECTION"}
          </Button>
        </div>
      )}
    </div>
  );
};

const LiveRecoveryPanel = ({ remaining, total, currentVx }) => {
  const pct = total > 0 ? Math.min(100, Math.max(0, ((total - remaining) / total) * 100)) : 0;
  const vx = Number(currentVx ?? 0);
  const converging = vx < 1.0;
  return (
    <div
      data-testid="live-recovery-panel"
      className="flex-1 flex flex-col"
    >
      <div className="rounded-lg border border-amber-700/50 bg-amber-950/20 p-3 mb-3 flex items-center gap-2">
        <Clock className="h-4 w-4 text-amber-400 shrink-0 animate-pulse" />
        <p className="mono text-[11px] text-amber-300/90">
          Correction sealed · driving V(x) back below threshold before resuming
        </p>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center text-center py-2">
        <p className="mono text-[10px] uppercase tracking-widest text-amber-300 mb-2 flex items-center gap-1.5">
          <Clock className="h-3 w-3" /> Estimated Recovery Window
        </p>
        <div
          data-testid="recovery-countdown"
          className="mono text-5xl font-extrabold tabular-nums text-amber-200 leading-none"
        >
          {remaining}s
        </div>
        <p className="mono text-[10px] text-slate-500 mt-2">remaining until V(x) &lt; 1.00</p>

        <div className="w-full max-w-xs mt-5">
          <div className="relative h-2.5 rounded-full bg-slate-800 overflow-hidden">
            <div
              data-testid="recovery-progress-bar"
              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-amber-400 to-emerald-400 transition-[width] duration-1000 ease-linear"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex justify-between mono text-[9px] text-slate-500 mt-1.5">
            <span>fault intercepted</span>
            <span>nominal</span>
          </div>
        </div>

        <div
          data-testid="recovery-live-vx"
          className="mt-6 inline-flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-950/50 px-4 py-2.5"
        >
          <span className="mono text-[10px] uppercase tracking-widest text-slate-500">V(x) live</span>
          <span
            className="mono text-lg font-extrabold tabular-nums"
            style={{ color: converging ? "#22d3ee" : "#fbbf24" }}
          >
            {vx.toFixed(4)}
          </span>
          <span className="mono text-[10px] text-slate-600">/ 1.00</span>
        </div>

        <p className="mono text-[10px] text-slate-500 mt-5 max-w-xs leading-relaxed">
          Analysis, library capture and audit-store sync unlock once the system
          reaches a green nominal state.
        </p>
      </div>
    </div>
  );
};

const RecoveryWindow = ({ spikeValue }) => {
  const { seconds, projected } = useMemo(() => {
    const { seconds } = estimateRecovery(spikeValue);
    return { seconds, projected: new Date(Date.now() + seconds * 1000) };
  }, [spikeValue]);
  const clock = projected.toISOString().replace("T", " ").slice(11, 19) + "Z";
  return (
    <div
      data-testid="recovery-window"
      className="rounded-lg border border-cyan-800/40 bg-cyan-950/15 p-3 mb-4"
    >
      <div className="flex items-center justify-between mb-2">
        <p className="mono text-[10px] uppercase tracking-widest text-cyan-300 flex items-center gap-1.5">
          <Clock className="h-3 w-3" /> Estimated Recovery Window
        </p>
        <span className="mono text-[11px] text-cyan-200 font-semibold">~{seconds}s</span>
      </div>
      <div className="relative h-2 rounded-full bg-slate-800 overflow-hidden mb-1.5">
        <div className="absolute inset-y-0 left-0 w-full rounded-full bg-gradient-to-r from-red-500 via-amber-400 to-emerald-400" />
      </div>
      <div className="flex justify-between mono text-[9px] text-slate-500">
        <span>now · V(x)={spikeValue}</span>
        <span>V(x) &lt; 1.00 projected ≈ {clock}</span>
      </div>
    </div>
  );
};

const Detail = ({ label, value, danger }) => (
  <div className="rounded-lg border border-slate-800 bg-slate-950/40 px-3 py-2">
    <p className="mono text-[9px] uppercase tracking-widest text-slate-500">{label}</p>
    <p className={`mono text-xs font-semibold mt-0.5 truncate ${danger ? "text-red-300" : "text-slate-200"}`}>
      {value}
    </p>
  </div>
);

const BreakdownField = ({ label, value }) => (
  <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
    <div className="flex items-center gap-1.5 mb-1">
      <FileText className="h-3 w-3 text-cyan-400" />
      <span className="mono text-[10px] uppercase tracking-widest text-cyan-400">{label}</span>
    </div>
    <p className="text-xs text-slate-300 leading-relaxed">{value}</p>
  </div>
);

const SyncPanel = ({ sealed }) => {
  const [synced, setSynced] = useState(false);
  const payload = useMemo(() => buildSyncPayload(sealed), [sealed?.id]);
  return (
    <div className="mt-3 border-t border-slate-800 pt-3">
      {!synced ? (
        <Button
          data-testid="sync-audit-store-button"
          onClick={() => setSynced(true)}
          variant="outline"
          className="w-full h-10 mono font-bold tracking-widest border-teal-700/50 bg-teal-950/20 text-teal-200 hover:border-teal-500/70 hover:text-teal-100 transition-all"
        >
          <UploadCloud className="h-4 w-4 mr-2" /> SYNC TO SECURE AUDIT STORE
        </Button>
      ) : (
        <div data-testid="sync-status" className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="mono text-[10px] uppercase tracking-widest text-teal-300">
              Secure Audit Store · Ingestion
            </span>
            <span className="inline-flex items-center gap-1 mono text-[10px] font-semibold text-emerald-300">
              <CheckCircle2 className="h-3 w-3" /> COMMITTED_200_OK
            </span>
          </div>
          <pre className="rounded-lg bg-slate-950 border border-slate-800 p-3 mono text-[10px] text-cyan-200 overflow-x-auto leading-relaxed max-h-[300px] overflow-y-auto">
{JSON.stringify(payload, null, 2)}
          </pre>
          <p className="mono text-[10px] text-slate-500 leading-relaxed">
            In production, this payload is flushed to immutable cold storage on each audit batch close.
          </p>
        </div>
      )}
    </div>
  );
};

const AnalyzePanel = ({ lastSealed, analysis, onAnalyze, onSave, saving, saved }) => (
  <div className="flex-1 flex flex-col">
    <div className="rounded-lg border border-emerald-800/50 bg-emerald-950/20 p-3 mb-3 flex items-center gap-2">
      <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
      <p className="mono text-[11px] text-emerald-300/90">
        Correction sealed · entry #{String(lastSealed.batch_seq).padStart(3, "0")} · Canonical
      </p>
    </div>

    {!analysis ? (
      <div className="flex-1 flex flex-col items-center justify-center text-center py-4">
        <Sparkles className="h-8 w-8 text-cyan-400 mb-3" />
        <p className="text-sm text-slate-300 font-medium">Fault event ready for analysis</p>
        <p className="mono text-[11px] text-slate-500 mt-1 mb-4 max-w-xs">
          Generate a structured breakdown of this event, grounded strictly in its sealed
          telemetry and signatures.
        </p>
        <Button
          data-testid="analyze-button"
          onClick={onAnalyze}
          className="h-10 mono font-bold tracking-widest bg-cyan-500 hover:bg-cyan-400 text-[#060A12]"
        >
          <Sparkles className="h-4 w-4 mr-2" /> ANALYZE FAULT EVENT
        </Button>
      </div>
    ) : (
      <div className="flex-1 flex flex-col">
        <div className="space-y-2.5 flex-1 overflow-y-auto pr-1 max-h-[320px]" data-testid="analysis-breakdown">
          <BreakdownField label="Fault Type" value={analysis.fault_type} />
          <BreakdownField label="Governor Action" value={analysis.governor_action} />
          <BreakdownField label="Correction Applied" value={analysis.correction_applied} />
          <BreakdownField label="Outcome" value={analysis.outcome} />
        </div>
        <Button
          data-testid="save-library-button"
          onClick={onSave}
          disabled={saving || saved}
          className={`mt-3 h-10 mono font-bold tracking-widest ${
            saved
              ? "bg-emerald-600 hover:bg-emerald-600 text-white"
              : "bg-teal-500 hover:bg-teal-400 text-[#060A12]"
          }`}
        >
          {saved ? (
            <><CheckCircle2 className="h-4 w-4 mr-2" /> SAVED TO LIBRARY</>
          ) : (
            <><BookmarkPlus className="h-4 w-4 mr-2" /> {saving ? "SAVING…" : "SAVE TO LIBRARY"}</>
          )}
        </Button>
      </div>
    )}
    <SyncPanel key={lastSealed.id} sealed={lastSealed} />
  </div>
);
